import { type ReactElement, type ReactNode, useId } from 'react';
import { Link } from 'react-router';
import { useMyQuotesQuery } from '@/entities/quote';
import { useAuthStore } from '@/entities/user';
import { routeForStatus } from '@/features/auth';
import { formatCount, getErrorMessage, getTrpcErrorCode } from '@/shared/lib';
import { Button, Pagination } from '@/shared/ui';
import { MyQuoteEmpty } from './my-quote-empty';
import { MyQuoteListSkeleton } from './my-quote-list-skeleton';
import { MyQuoteTable } from './my-quote-table';

export interface MyQuoteListProps {
  readonly page: number;
  /** 페이지 이동 — 페이지가 URL 쿼리(page)를 바꾼다. */
  readonly onPageChange: (page: number) => void;
}

const ONBOARDING_PATH = '/onboarding';

/** 오류·범위 밖 안내 — 기획전 오류와 같은 얇은 위아래 선 사이의 가운데 정렬 칸. */
const NOTICE_CLASS =
  'flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center';

/**
 * 내 견적 목록(견적 설계 §7-1) — 견적 번호·상품명·수량·요청일·상태(주문된 견적은 주문 고객 화면 이름)·최신 발행본 합계.
 * 본인인증 전 회원은 서버(activeProcedure)가 FORBIDDEN 으로 거부한다 — 다시 시도 대신 서버 사유와(인증 대기면) 온보딩 안내를 보여 준다.
 * 목록 API 는 페이지 번호만 받는다 — 상태별 탭·건수는 서버가 주지 않으므로 전체 건수만 보여 준다.
 */
export const MyQuoteList = ({ page, onPageChange }: MyQuoteListProps): ReactElement => {
  const query = useMyQuotesQuery({ page });
  const accountStatus = useAuthStore((state) => state.user?.status);
  const headingId = useId();

  // 받은 목록이 있으면 다시 조회(그림 기다림·창 포커스) 실패는 조용히 넘긴다 — 오류 화면은 아직 받은 것이 없을 때만이다.
  const failedError = query.data === undefined ? query.error : null;

  const content = ((): ReactNode => {
    if (query.isPending) {
      return <MyQuoteListSkeleton />;
    }

    if (failedError !== null && getTrpcErrorCode(failedError) === 'FORBIDDEN') {
      const needsOnboarding =
        accountStatus !== undefined && routeForStatus(accountStatus) === ONBOARDING_PATH;

      return (
        <div role="alert" className={NOTICE_CLASS}>
          <p className="text-sm text-destructive">
            {getErrorMessage(failedError, '내 견적을 볼 수 없는 계정 상태입니다.')}
          </p>
          {needsOnboarding ? (
            <Link
              to={ONBOARDING_PATH}
              className="text-sm font-semibold underline underline-offset-4 hover:no-underline"
            >
              인증 마치러 가기
            </Link>
          ) : null}
        </div>
      );
    }

    if (query.data === undefined) {
      return (
        <div role="alert" className={NOTICE_CLASS}>
          <p className="text-sm text-destructive">내 견적을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      );
    }

    const { items, meta } = query.data;
    const lastPage = Math.max(1, meta.totalPages);

    if (items.length === 0 && meta.total > 0 && page > meta.totalPages) {
      return (
        <div className={NOTICE_CLASS}>
          <p role="status" className="text-sm text-muted-foreground">
            페이지 범위를 벗어났습니다.
          </p>
          <Button variant="outline" size="sm" onClick={() => onPageChange(lastPage)}>
            마지막 페이지로
          </Button>
        </div>
      );
    }

    if (items.length === 0) {
      return <MyQuoteEmpty />;
    }

    return (
      <div className="flex flex-col gap-10">
        <MyQuoteTable rows={items} />
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          onPageChange={onPageChange}
          className="justify-center"
        />
      </div>
    );
  })();

  const total = query.data?.meta.total;

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2
            id={headingId}
            className="text-2xl leading-tight font-bold tracking-[-0.02em] sm:text-[1.75rem]"
          >
            내 견적
          </h2>
          <p className="text-sm text-muted-foreground">
            요청한 견적의 진행 상태와 발행된 견적 금액을 확인합니다.
          </p>
        </div>
        {total !== undefined && total > 0 ? (
          <p className="text-sm text-muted-foreground">
            전체{' '}
            <span className="font-semibold text-foreground tabular-nums">{formatCount(total)}</span>
            건
          </p>
        ) : null}
      </div>
      {content}
    </section>
  );
};
